import { useMutation, useQuery } from "@apollo/client";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import SearchInput from "@dashboard/components/SearchBar/SearchInput";
import useNotifier from "@dashboard/hooks/useNotifier";
import { sectionNames } from "@dashboard/intl";
import { Box, Button, Select, Text } from "@saleor/macaw-ui-next";
import React, { Component, ErrorInfo, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { HtmlPageCard } from "../components/HtmlPageCard";
import { DELETE_SHOP_METADATA, UPDATE_SHOP_METADATA } from "../mutations";
import { HTML_WIDGETS } from "../queries";
import { HtmlPage } from "../types";
import { htmlManagerAddUrl, htmlManagerUrl } from "../urls";
import { filterHtmlPages, parseHtmlPageFromMetadata, serializeHtmlPageToMetadata } from "../utils";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('HTML Manager Error Details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorBoundary: 'HtmlManagerListView'
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ListPageLayout>
          <TopNav title="HTML Manager" />
          <DashboardCard>
            <DashboardCard.Content>
              <Box padding={8} textAlign="center">
                <Text size={3} color="critical1">Error in HTML Manager</Text>
                <Text size={2} marginTop={2}>
                  {this.state.error?.message || 'Unknown error occurred'}
                </Text>
                <Button 
                  variant="primary" 
                  marginTop={4}
                  onClick={() => this.setState({ hasError: false })}
                >
                  Try Again
                </Button>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
        </ListPageLayout>
      );
    }

    return this.props.children;
  }
}

export interface HtmlManagerListViewProps {
  sort?: any;
}

export const HtmlManagerListView: React.FC<HtmlManagerListViewProps> = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>({});

  const { data, loading, refetch, error: queryError } = useQuery(HTML_WIDGETS, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    onError: (error) => {
      console.error("GraphQL Query Error:", error);
      notify({
        status: "error",
        text: "Failed to load pages. Please refresh the page.",
      });
    },
  });

  const [updateMetadata] = useMutation(UPDATE_SHOP_METADATA);
  const [deleteMetadata] = useMutation(DELETE_SHOP_METADATA);

  const pages = useMemo(() => {
    try {
      if (!data?.shop?.metadata) {
        return [];
      }
      
      const parsedPages = data.shop.metadata
        .map((item: any) => {
          try {
            return parseHtmlPageFromMetadata(item.key, item.value);
          } catch (parseError) {
            console.error("Error parsing individual page:", item, parseError);

            return null;
          }
        })
        .filter((page: HtmlPage | null): page is HtmlPage => page !== null);
      
      return parsedPages;
    } catch (error) {
      notify({
        status: "error", 
        text: "Error parsing page data"
      });

      return [];
    }
  }, [data, notify]);

  const filteredPages = useMemo(() => {
    return filterHtmlPages(pages, {
      search,
      isActive: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
    });
  }, [pages, search, statusFilter]);

  const handleEditPage = (page: HtmlPage) => {
    window.location.href = htmlManagerUrl(page.id);
  };

  const handleDeletePage = async (page: HtmlPage) => {
    if (!confirm(`Are you sure you want to delete "${page.name}"? This action cannot be undone.`)) {
      return;
    }

    const actionKey = `delete_${page.id}`;

    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));

    try {
      const shopId = data?.shop?.id;

      if (!shopId) {
        notify({
          status: "error",
          text: "Error: Shop ID not found",
        });

        return;
      }

      await deleteMetadata({
        variables: {
          id: shopId,
          keys: [`html_widget_${page.id}`],
        },
      });

      await refetch();
      notify({
        status: "success",
        text: `Page "${page.name}" has been deleted successfully.`,
      });
    } catch (error) {
      notify({
        status: "error",
        text: "Failed to delete page. Please try again.",
      });
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleToggleStatus = async (page: HtmlPage) => {
    const actionKey = `toggle_${page.id}`;

    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));

    try {
      const shopId = data?.shop?.id;

      if (!shopId) {
        notify({
          status: "error",
          text: "Error: Shop ID not found",
        });

        return;
      }

      const updatedPage: HtmlPage = {
        ...page,
        isActive: !page.isActive,
        updatedAt: new Date().toISOString(),
      };

      const { key, value } = serializeHtmlPageToMetadata(updatedPage);

      await updateMetadata({
        variables: {
          id: shopId,
          input: [{ key, value }],
        },
      });

      await refetch();
      
      const statusText = updatedPage.isActive ? "activated" : "deactivated";

      notify({
        status: "success",
        text: `Page "${page.name}" has been ${statusText}.`,
      });
    } catch (error) {
      notify({
        status: "error",
        text: "Failed to update page status. Please try again.",
      });
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  if (loading) {
    return (
      <ListPageLayout>
        <TopNav title={intl.formatMessage(sectionNames.htmlManager)}>
          <Button 
            variant="primary" 
            onClick={() => window.location.href = htmlManagerAddUrl()}
          >
            Create Page
          </Button>
        </TopNav>
        <DashboardCard>
          <DashboardCard.Content>
            <Box padding={8} textAlign="center">
              <Text size={3}>Loading HTML Manager...</Text>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </ListPageLayout>
    );
  }

  if (queryError) {
    console.error("Query error detected:", queryError);

    return (
      <ListPageLayout>
        <TopNav title={intl.formatMessage(sectionNames.htmlManager)}>
          <Button 
            variant="primary" 
            onClick={() => window.location.href = htmlManagerAddUrl()}
          >
            Create Page
          </Button>
        </TopNav>
        <DashboardCard>
          <DashboardCard.Content>
            <Box padding={8} textAlign="center">
              <Text size={3} color="critical1">GraphQL Error</Text>
              <Text size={2} marginTop={2}>
                {queryError.message}
              </Text>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </ListPageLayout>
    );
  }

  return (
    <ErrorBoundary>
      <ListPageLayout>
        <Box 
          style={{ 
            position: "sticky", 
            top: 0, 
            zIndex: 100,
            backgroundColor: "white",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
          }}
        >
          <TopNav title={intl.formatMessage(sectionNames.htmlManager)}>
            <Button 
              variant="primary" 
              onClick={() => window.location.href = htmlManagerAddUrl()}
            >
              Create Page
            </Button>
          </TopNav>

          <Box 
            paddingX={6}
            paddingY={4}
            style={{
              backgroundColor: "white",
              borderBottom: "1px solid #e5e7eb"
            }}
          >
            <Box 
              display="flex" 
              gap={3} 
              alignItems="center"
              __flexDirection={{ mobile: "column", tablet: "row" }}
            >
              <Box __flex="1" __minWidth="0">
                <SearchInput
                  initialSearch={search}
                  onSearchChange={setSearch}
                  placeholder="Search pages..."
                />
              </Box>
              
              <Box __flex="0 0 160px">
                <Select
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as string)}
                  options={[
                    { label: "All Status", value: "" },
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                  ]}
                />
              </Box>
            </Box>

            {!loading && (
              <Box marginTop={3}>
                <Text color="default2" size={2}>
                  {filteredPages.length === pages.length 
                    ? `${pages.length} page${pages.length !== 1 ? 's' : ''}`
                    : `${filteredPages.length} of ${pages.length} pages`
                  }
                  {filteredPages.length !== pages.length && (
                    <Button
                      variant="tertiary"
                      size="small"
                      marginLeft={2}
                      onClick={() => {
                        setSearch("");
                        setStatusFilter("");
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        <Box 
          paddingX={6} 
          paddingY={6}
          style={{
            backgroundColor: "#f8fafc",
            minHeight: "calc(100vh - 200px)"
          }}
        >
          {loading ? (
            <Box 
              padding={12} 
              textAlign="center"
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
              }}
            >
              <Text size={4}>Loading pages...</Text>
            </Box>
          ) : filteredPages.length === 0 ? (
            <Box
              padding={12} 
              textAlign="center"
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                border: "2px dashed #d1d5db",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
              }}
            >
              <Text size={6} marginBottom={3} style={{ fontSize: "48px" }}>
                {pages.length === 0 ? "🎨" : "🔍"}
              </Text>
              <Text size={4} fontWeight="bold" marginBottom={2} color="default1">
                {pages.length === 0 
                  ? "No HTML pages yet"
                  : "No matching pages"
                }
              </Text>
              <Text color="default2" size={3} marginBottom={6} __lineHeight="1.6">
                {pages.length === 0 
                  ? "Create your first HTML page to add custom calculators, forms, and interactive content to your storefront."
                  : "Try adjusting your search criteria or clearing the filters."
                }
              </Text>
              {pages.length === 0 ? (
                <Button 
                  variant="primary" 
                  size="large"
                  onClick={() => window.location.href = htmlManagerAddUrl()}
                >
                  Create Your First Page
                </Button>
              ) : (
                <Button 
                  variant="secondary" 
                  size="large"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={0}>
              {filteredPages.map((page, index) => (
                <Box key={page.id} style={{ marginBottom: index === filteredPages.length - 1 ? "0" : "24px" }}>
                  <HtmlPageCard
                    page={page}
                    onEdit={() => handleEditPage(page)}
                    onDelete={() => handleDeletePage(page)}
                    onToggleStatus={() => handleToggleStatus(page)}
                    isDeleting={loadingActions[`delete_${page.id}`] || false}
                    isToggling={loadingActions[`toggle_${page.id}`] || false}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </ListPageLayout>
    </ErrorBoundary>
  );
};